import { useState } from "react";
import { AdminButton } from "@/components/admin/button";
import { Card, CardHead } from "@/components/admin/card";
import { AdminIcon, type IconName } from "@/components/admin/icon";
import { Kpi } from "@/components/admin/kpi";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { usePermissions } from "@/hooks/use-permissions";
import { customersApi } from "@/lib/customers/customers-api";
import {
	useCreateReward,
	useLoyaltyLiability,
	useLoyaltyRewards,
	useLoyaltyTiers,
} from "@/lib/customers/customers-hooks";
import type { LoyaltyReward, LoyaltyTier } from "@/lib/customers/types";
import { fmtFCFA } from "@/lib/format";

export function AdminCustomerLoyaltyPage() {
	const toast = useToast();
	const perms = usePermissions();
	const {
		data: tiers,
		isLoading: isTiersLoading,
		isError: isTiersError,
		refetch: refetchTiers,
	} = useLoyaltyTiers();
	const { data: liability, isLoading: isLiabLoading } = useLoyaltyLiability();
	const {
		data: rewards,
		isLoading: isRewardsLoading,
		isError: isRewardsError,
		refetch: refetchRewards,
	} = useLoyaltyRewards();
	const createRewardMutation = useCreateReward();

	const canEditConfig = perms.has("LOYALTY.CONFIG") || perms.isSuperAdmin;

	// State modale édition palier
	const [editingTier, setEditingTier] = useState<LoyaltyTier | null>(null);
	const [editForm, setEditForm] = useState({
		pointsRatio: "1.000",
		thresholdOrders12m: "",
		thresholdRevenue12m: "",
	});
	const [isSavingTier, setIsSavingTier] = useState(false);

	// State modale création récompense
	const [showRewardModal, setShowRewardModal] = useState(false);
	const [rewardForm, setRewardForm] = useState({
		label: "",
		pointsCost: "",
		valueFcfa: "",
		description: "",
	});

	const handleOpenEditTier = (t: LoyaltyTier) => {
		setEditingTier(t);
		setEditForm({
			pointsRatio: String(t.pointsRatio),
			thresholdOrders12m:
				t.thresholdOrders12m != null ? String(t.thresholdOrders12m) : "",
			thresholdRevenue12m: t.thresholdRevenue12m
				? String(t.thresholdRevenue12m)
				: "",
		});
	};

	const handleSaveTier = async () => {
		if (!editingTier) return;
		setIsSavingTier(true);
		try {
			await customersApi.updateTier(editingTier.id, {
				pointsRatio: editForm.pointsRatio,
				thresholdOrders12m: editForm.thresholdOrders12m
					? Number(editForm.thresholdOrders12m)
					: null,
				thresholdRevenue12m: editForm.thresholdRevenue12m || null,
			});
			toast.success(`Palier ${editingTier.label} mis à jour.`);
			setEditingTier(null);
			refetchTiers();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(
				ax.response?.data?.message ?? "Erreur de mise à jour du palier.",
			);
		} finally {
			setIsSavingTier(false);
		}
	};

	const handleCreateReward = async () => {
		if (!rewardForm.label || !rewardForm.pointsCost) {
			toast.error("Nom et coût en points obligatoires.");
			return;
		}
		try {
			await createRewardMutation.mutateAsync({
				label: rewardForm.label,
				pointsCost: Number(rewardForm.pointsCost),
				valueFcfa: rewardForm.valueFcfa || undefined,
				description: rewardForm.description || undefined,
				isActive: true,
			});
			toast.success(`Récompense « ${rewardForm.label} » créée avec succès.`);
			setShowRewardModal(false);
			setRewardForm({ label: "", pointsCost: "", valueFcfa: "", description: "" });
			refetchRewards();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(
				ax.response?.data?.message ?? "Échec de création de la récompense.",
			);
		}
	};

	return (
		<>
			<PageHead
				title="Programme fidélité"
				sub="Paliers, ratios et passif points (CUS-05)"
				actions={
					canEditConfig && (
						<AdminButton
							variant="primary"
							onClick={() => setShowRewardModal(true)}
						>
							<AdminIcon n="plus" s={14} />
							Créer une récompense
						</AdminButton>
					)
				}
			/>

			<div className="kpi-grid">
				<Kpi
					label="Passif total"
					variant="accent"
					value={
						isLiabLoading
							? "—"
							: `${(liability?.totalPoints ?? 0).toLocaleString("fr-FR")} pts`
					}
					delta="points en circulation"
					deltaVariant="neutral"
				/>
				<Kpi
					label="Comptes fidélité"
					value={
						isLiabLoading
							? "—"
							: `${(liability?.accounts ?? 0).toLocaleString("fr-FR")} comptes`
					}
				/>
				<Kpi
					label="Passif Gold"
					variant="forest"
					value={
						isLiabLoading
							? "—"
							: `${(liability?.byTier?.gold ?? 0).toLocaleString("fr-FR")} pts`
					}
				/>
				<Kpi
					label="Passif Platinum"
					variant="accent"
					value={
						isLiabLoading
							? "—"
							: `${(liability?.byTier?.platinum ?? 0).toLocaleString("fr-FR")} pts`
					}
				/>
			</div>

			<Card style={{ marginBottom: 24 }}>
				<CardHead
					title="Paliers de fidélité"
					sub="Ratio points / 100 FCFA · seuils 12 mois glissants"
				/>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Palier</th>
								<th className="right">Ratio</th>
								<th className="right">Seuil commandes</th>
								<th className="right">Seuil CA</th>
								<th>Accession</th>
								<th className="right">Passif (pts)</th>
								{canEditConfig && <th className="right">Actions</th>}
							</tr>
						</thead>
						<tbody>
							{isTiersLoading && (
								<tr>
									<td
										colSpan={7}
										className="muted"
										style={{ textAlign: "center", padding: 24 }}
									>
										Chargement des paliers…
									</td>
								</tr>
							)}
							{isTiersError && (
								<tr>
									<td
										colSpan={7}
										className="muted"
										style={{ textAlign: "center", padding: 24 }}
									>
										Erreur de chargement des paliers.
									</td>
								</tr>
							)}
							{!isTiersLoading &&
								!isTiersError &&
								(tiers ?? []).map((t) => {
									const badge = getTierBadge(t.key);
									return (
										<tr key={t.id}>
											<td>
												<span
													style={{
														display: "inline-flex",
														alignItems: "center",
														gap: 8,
														padding: "4px 10px",
														borderRadius: 6,
														fontSize: 13,
														fontWeight: 600,
														background: badge.bg,
														color: badge.color,
														border: `1px solid ${badge.border}`,
													}}
												>
													<AdminIcon n={badge.icon} s={14} />
													{t.label}
												</span>
											</td>
											<td className="right tn">
												<strong>{Number(t.pointsRatio)} pt</strong> / 100 FCFA
											</td>
											<td className="right tn">
												{t.thresholdOrders12m != null ? (
													<span>{t.thresholdOrders12m} cmde{t.thresholdOrders12m > 1 ? "s" : ""}</span>
												) : (
													<span className="muted">—</span>
												)}
											</td>
											<td className="right tn">
												{t.thresholdRevenue12m
													? fmtFCFA(Number(t.thresholdRevenue12m))
													: <span className="muted">—</span>}
											</td>
											<td>
												{t.manualOnly ? (
													<Tag variant="terra" noDot>
														Cooptation
													</Tag>
												) : (
													<Tag variant="neutral" noDot>
														Automatique
													</Tag>
												)}
											</td>
											<td className="right tn">
												<strong>
													{(liability?.byTier?.[t.key] ?? 0).toLocaleString(
														"fr-FR",
													)}
												</strong>{" "}
												pts
											</td>
											{canEditConfig && (
												<td className="right">
													<AdminButton
														variant="ghost"
														onClick={() => handleOpenEditTier(t)}
													>
														Modifier
													</AdminButton>
												</td>
											)}
										</tr>
									);
								})}
						</tbody>
					</table>
				</div>
			</Card>

			{/* Section Catalogue des Récompenses */}
			<Card>
				<CardHead
					title="Catalogue des récompenses"
					sub="Bons d'achat et avantages échangeables par les membres fidélité"
				/>
				<div className="table-wrap" style={{ margin: 0 }}>
					{isRewardsLoading && (
						<div
							className="muted"
							style={{ textAlign: "center", padding: 24 }}
						>
							Chargement du catalogue des récompenses…
						</div>
					)}
					{isRewardsError && (
						<div
							className="muted"
							style={{ textAlign: "center", padding: 24 }}
						>
							Erreur de chargement du catalogue des récompenses.
						</div>
					)}
					{!isRewardsLoading &&
						!isRewardsError &&
						(!rewards || rewards.length === 0 ? (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									gap: 12,
									padding: "48px 24px",
									textAlign: "center",
								}}
							>
								<span
									style={{
										display: "inline-flex",
										padding: 14,
										borderRadius: "50%",
										background: "var(--a-surface-muted, #f4f6f8)",
										color: "var(--a-ink-soft)",
									}}
								>
									<AdminIcon n="star" s={28} />
								</span>
								<div>
									<h4
										style={{
											margin: 0,
											fontSize: 15,
											fontWeight: 600,
											color: "var(--a-ink)",
										}}
									>
										Aucune récompense configurée
									</h4>
									<p
										style={{
											margin: "4px 0 0",
											fontSize: 13,
											color: "var(--a-ink-soft)",
											maxWidth: 400,
										}}
									>
										Créez votre première récompense pour permettre aux clients d'échanger leurs points cumulés contre des avantages.
									</p>
								</div>
								{canEditConfig && (
									<AdminButton
										variant="primary"
										onClick={() => setShowRewardModal(true)}
										style={{ marginTop: 8 }}
									>
										<AdminIcon n="plus" s={14} />
										Créer une récompense
									</AdminButton>
								)}
							</div>
						) : (
							<table className="table">
								<thead>
									<tr>
										<th>Nom de la récompense</th>
										<th className="right">Coût</th>
										<th className="right">Valeur FCFA</th>
										<th>Description</th>
										<th>Statut</th>
									</tr>
								</thead>
								<tbody>
									{rewards.map((r: LoyaltyReward) => (
										<tr key={r.id}>
											<td>
												<strong>{r.label}</strong>
											</td>
											<td className="right tn">
												<strong>{r.pointsCost.toLocaleString("fr-FR")}</strong> pts
											</td>
											<td className="right tn">
												{r.valueFcfa
													? fmtFCFA(Number(r.valueFcfa))
													: <span className="muted">—</span>}
											</td>
											<td>
												{r.description ? (
													<span style={{ fontSize: 13, color: "var(--a-ink-soft)" }}>
														{r.description}
													</span>
												) : (
													<span className="muted">—</span>
												)}
											</td>
											<td>
												{r.isActive ? (
													<Tag variant="success" noDot>
														Actif
													</Tag>
												) : (
													<Tag variant="neutral" noDot>
														Inactif
													</Tag>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						))}
				</div>
			</Card>

			{/* Modale d'édition de palier */}
			{editingTier && (
				<Modal
					title={`Modifier le palier ${editingTier.label}`}
					subtitle="Ajustez les critères de cumul et les seuils de franchissement pour ce palier."
					onClose={() => setEditingTier(null)}
				>
					<div style={{ display: "grid", gap: 14 }}>
						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink)" }}>
								Ratio points / 100 FCFA *
							</span>
							<input
								className="inp"
								value={editForm.pointsRatio}
								onChange={(e) => setEditForm({ ...editForm, pointsRatio: e.target.value })}
							/>
						</label>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink)" }}>
								Seuil commandes 12 mois glissants
							</span>
							<input
								className="inp"
								placeholder="Ex : 15"
								value={editForm.thresholdOrders12m}
								onChange={(e) =>
									setEditForm({ ...editForm, thresholdOrders12m: e.target.value })
								}
							/>
						</label>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink)" }}>
								Seuil chiffre d'affaires 12 mois glissants (FCFA)
							</span>
							<input
								className="inp"
								placeholder="Ex : 750000"
								value={editForm.thresholdRevenue12m}
								onChange={(e) =>
									setEditForm({ ...editForm, thresholdRevenue12m: e.target.value })
								}
							/>
						</label>
					</div>

					<ModalFooter>
						<AdminButton variant="ghost" onClick={() => setEditingTier(null)}>
							Annuler
						</AdminButton>
						<AdminButton
							variant="primary"
							disabled={isSavingTier}
							onClick={handleSaveTier}
						>
							{isSavingTier ? "Enregistrement…" : "Enregistrer la modification"}
						</AdminButton>
					</ModalFooter>
				</Modal>
			)}

			{/* Modale de création de récompense */}
			{showRewardModal && (
				<Modal
					title="Créer une récompense"
					subtitle="Configurez une nouvelle récompense échangeable contre des points de fidélité."
					onClose={() => setShowRewardModal(false)}
				>
					<div style={{ display: "grid", gap: 14 }}>
						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink)" }}>
								Nom de la récompense *
							</span>
							<input
								className="inp"
								placeholder="Ex : Bon d'achat 5 000 FCFA"
								value={rewardForm.label}
								onChange={(e) =>
									setRewardForm({ ...rewardForm, label: e.target.value })
								}
							/>
						</label>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink)" }}>
								Coût en points *
							</span>
							<input
								className="inp"
								type="number"
								placeholder="Ex : 500"
								value={rewardForm.pointsCost}
								onChange={(e) =>
									setRewardForm({ ...rewardForm, pointsCost: e.target.value })
								}
							/>
						</label>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink)" }}>
								Valeur en FCFA (optionnel)
							</span>
							<input
								className="inp"
								placeholder="Ex : 5000"
								value={rewardForm.valueFcfa}
								onChange={(e) =>
									setRewardForm({ ...rewardForm, valueFcfa: e.target.value })
								}
							/>
						</label>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink)" }}>
								Description
							</span>
							<input
								className="inp"
								placeholder="Ex : Valable sur tout le catalogue pendant 30 jours"
								value={rewardForm.description}
								onChange={(e) =>
									setRewardForm({ ...rewardForm, description: e.target.value })
								}
							/>
						</label>
					</div>

					<ModalFooter>
						<AdminButton
							variant="ghost"
							onClick={() => setShowRewardModal(false)}
						>
							Annuler
						</AdminButton>
						<AdminButton
							variant="primary"
							disabled={
								createRewardMutation.isPending ||
								!rewardForm.label ||
								!rewardForm.pointsCost
							}
							onClick={handleCreateReward}
						>
							{createRewardMutation.isPending
								? "Création…"
								: "Créer la récompense"}
						</AdminButton>
					</ModalFooter>
				</Modal>
			)}
		</>
	);
}

function getTierBadge(key: string): { bg: string; color: string; border: string; icon: IconName } {
	switch (key.toLowerCase()) {
		case "bronze":
			return {
				bg: "rgba(180, 83, 9, 0.08)",
				color: "#92400e",
				border: "rgba(180, 83, 9, 0.2)",
				icon: "star",
			};
		case "silver":
			return {
				bg: "rgba(71, 85, 105, 0.08)",
				color: "#475569",
				border: "rgba(71, 85, 105, 0.2)",
				icon: "star",
			};
		case "gold":
			return {
				bg: "rgba(217, 119, 6, 0.12)",
				color: "#b45309",
				border: "rgba(217, 119, 6, 0.25)",
				icon: "star",
			};
		case "platinum":
			return {
				bg: "rgba(124, 58, 237, 0.1)",
				color: "#6d28d9",
				border: "rgba(124, 58, 237, 0.25)",
				icon: "shield",
			};
		default:
			return {
				bg: "var(--a-surface-muted, #f4f6f8)",
				color: "var(--a-ink)",
				border: "var(--a-border)",
				icon: "star",
			};
	}
}
